/**
 * MembresTable - Tableau des membres avec lignes depliables
 * Utilise dans tous les espaces: Cooper, Admin, Super Admin, Expert
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Users, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search, Filter, Trash2, FileDown, Loader2, ChevronsLeft, ChevronsRight, XCircle} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

import { STATUS_CONFIG, RCDS_CONFIG, FTPE_CONFIG, DOS_CONFIG, PAGE_SIZE_OPTIONS } from './configs';
import { StatusBadge, RCDSBadge, FTPEBadge, DOSBadge, MemberAvatar } from './badges';
import { EditContactModal, SendFTPEAttestationModal, AssignCreateurModal, UploadDocumentMembreModal, UpdateDispositifsModal, EditFtpeDateModal, RemiseSolidaireModal } from './modals';
import { QualificationsModal } from './QualificationsModal';
import { exportToCSV } from './utils';
import { getToken } from '../../services/tokenService';
import MembreRow from './MembreRow';
import MemberSimulationsModal from './MemberSimulationsModal';
import SubscriptionDocumentsModal from './SubscriptionDocumentsModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MembresTable({
  membres = [],
  loading = false,
  onView,
  onEdit: onEdit,
  onDelete,
  onSendEmail,
  onViewDocuments,
  onCreateEstimation,
  onFTPEAction,
  onMemberUpdated,
  onRefresh,
  onImpersonate,
  showFTPE = true,
  showRCDS = true,
  showDOS = true,
  showEntreprise = true,
  showActions = true,
  showExportCSV = false,
  compactMode: compactMode = false,
  emptyMessage = "Aucun membre trouve",
  emptySubMessage = "Commencez par creer votre premier membre",
  userRole = 'member',
  token = null
}) {
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editContactMembre, setEditContactMembre] = useState(null);
  const [sendFTPEMembre, setSendFTPEMembre] = useState(null);
  const [assignCreateurMembre, setAssignCreateurMembre] = useState(null);
  const [uploadDocMembre, setUploadDocMembre] = useState(null);
  const [qualifsMembre, setQualifsMembre] = useState(null);
  const [dispositifsMembre, setDispositifsMembre] = useState(null);
  const [ftpeDateMembre, setFtpeDateMembre] = useState(null);
  const [remiseSolidaireMembre, setRemiseSolidaireMembre] = useState(null);
  const [simsDOSMembre, setSimsDOSMembre] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [docsSummary, setDocsSummary] = useState({});
  const [coppamResume, setCoppamResume] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [subDocsMembre, setSubDocsMembre] = useState(null);
  const [generatingReleveId, setGeneratingReleveId] = useState(null);
  const [generatingReleveFtpeId, setGeneratingReleveFtpeId] = useState(null);

  // Helper: identifiant unique du membre (fallback sur numero_membre pour anciens membres sans id)
  const getMid = (m) => m?.id || m?.numero_membre || '';

  const authToken = token || getToken();
  const canChangeStatus = ['admin', 'super_admin', 'cooper', 'expert'].includes(userRole);

  // V35.12 — Génération directe d'un Relevé d'information depuis le menu "Plus d'actions"
  // Réservé super_admin / admin. Téléchargement immédiat du PDF.
  const handleGenerateReleve = useCallback(async (membre) => {
    if (!membre?.id) return;
    setGeneratingReleveId(membre.id);
    try {
      const res = await axios.post(
        `${API}/api/admin/members/${membre.id}/releve-information/generate`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const { releve_id, download_url } = res.data || {};
      toast.success(`Relevé ${releve_id} généré`, {
        description: 'Téléchargement en cours…'});
      const dl = await axios.get(download_url, {
        headers: { Authorization: `Bearer ${authToken}` },
        responseType: 'blob'});
      const blob = new Blob([dl.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${releve_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la génération du relevé');
    } finally {
      setGeneratingReleveId(null);
    }
  }, [authToken]);

  // V35.14 — Génération directe d'un Relevé d'information FTPE depuis le menu "Plus d'actions"
  const handleGenerateReleveFtpe = useCallback(async (membre) => {
    if (!membre?.id) return;
    setGeneratingReleveFtpeId(membre.id);
    try {
      const res = await axios.post(
        `${API}/api/admin/members/${membre.id}/releve-information-ftpe/generate`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const { releve_id, download_url, cotisation_statut } = res.data || {};
      toast.success(`Relevé FTPE ${releve_id} généré`, {
        description: `Cotisation : ${cotisation_statut} · Téléchargement en cours…`});
      const dl = await axios.get(download_url, {
        headers: { Authorization: `Bearer ${authToken}` },
        responseType: 'blob'});
      const blob = new Blob([dl.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${releve_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la génération du relevé FTPE');
    } finally {
      setGeneratingReleveFtpeId(null);
    }
  }, [authToken]);

  // Listener pour ouvrir EditContactModal depuis l'extérieur (ex: bouton "Compléter le profil" dans DetailModal)
  useEffect(() => {
    const handleOpenEditContact = (e) => {
      const { membre, membreId } = e.detail || {};
      const target = membre || membres.find((m) => (m.id === membreId || m.numero_membre === membreId));
      if (target) setEditContactMembre(target);
    };
    window.addEventListener('open-edit-contact', handleOpenEditContact);
    return () => window.removeEventListener('open-edit-contact', handleOpenEditContact);
  }, [membres]);

  // Fetch documents summary for all members
  // V36.51 — élargi à COOPER/EXPERT (backend autorise déjà ces roles depuis V35).
  // Sans ce fix, l'icone "Certificat O'SCOP" disparaissait pour les COOPER's
  // qui consultent leur propre tableau membres.
  useEffect(() => {
    if (!authToken || !['super_admin', 'admin', 'cooper', 'expert'].includes(userRole)) return;
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API}/api/admin/membres/documents/summary`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        if (res.data?.summary) setDocsSummary(res.data.summary);
      } catch { /* silent */ }
    };
    fetchSummary();
    if (['super_admin', 'admin', 'cooper'].includes(userRole)) {
      axios.get(`${API}/api/members-adhesions-coppam/resume`, { headers: { Authorization: `Bearer ${authToken}` } })
        .then((res) => setCoppamResume(res.data?.resume || {})).catch(() => {});
    }
  }, [authToken, userRole]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleStatusChange = async (membre, newStatus) => {
    if (!membre || !newStatus || membre.status === newStatus) return;
    const mid = getMid(membre);
    setUpdatingStatusId(mid);
    try {
      const response = await axios.put(
        `${API}/api/admin/membres/${mid}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (response.data.membre && onMemberUpdated) onMemberUpdated(response.data.membre);
      toast.success(`Statut modifie: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors du changement de statut');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const filteredAndSortedMembres = useMemo(() => {
    let result = membres;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(m => 
        m.nom?.toLowerCase().includes(query) || m.prenom?.toLowerCase().includes(query) ||
        m.email?.toLowerCase().includes(query) || m.entreprise?.toLowerCase().includes(query) ||
        m.numero_membre?.toLowerCase().includes(query)
      );
    }
    if (statusFilter !== 'all') {
      if (statusFilter === 'en_attente') {
        result = result.filter(m => ['en_attente', 'en_attente_connexion', 'pending'].includes(m.status));
      } else {
        result = result.filter(m => m.status === statusFilter);
      }
    }
    return [...result].sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (sortField === 'nom') { aVal = `${a.nom || ''} ${a.prenom || ''}`.toLowerCase(); bVal = `${b.nom || ''} ${b.prenom || ''}`.toLowerCase(); }
      if (sortField === 'created_at' || sortField === 'date_creation') { aVal = new Date(aVal || 0).getTime(); bVal = new Date(bVal || 0).getTime(); }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [membres, searchQuery, statusFilter, sortField, sortDirection]);

  const paginationInfo = useMemo(() => {
    const totalItems = filteredAndSortedMembres.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);
    return {
      totalItems, totalPages, startIndex, endIndex,
      paginatedItems: filteredAndSortedMembres.slice(startIndex, endIndex),
      hasNextPage: currentPage < totalPages, hasPrevPage: currentPage > 1
    };
  }, [filteredAndSortedMembres, currentPage, pageSize]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };
  const handleSearchChange = useCallback((e) => { setSearchQuery(e.target.value); setCurrentPage(1); }, []);
  const handleStatusFilterChange = useCallback((value) => { setStatusFilter(value); setCurrentPage(1); }, []);
  const handlePageSizeChange = useCallback((value) => { setPageSize(Number(value)); setCurrentPage(1); }, []);
  const handleContactUpdated = useCallback((updatedMembre) => { if (onMemberUpdated && updatedMembre?.id) onMemberUpdated(updatedMembre); }, [onMemberUpdated]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const pageIds = paginationInfo.paginatedItems.map(m => getMid(m)).filter(Boolean);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.add(id)); return next; });
    }
  }, [paginationInfo.paginatedItems, selectedIds]);

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Etes-vous sur de vouloir supprimer ${count} membre(s) ? Cette action est irreversible.`)) return;
    setBatchDeleting(true);
    try {
      const res = await axios({
        method: 'delete',
        url: `${API}/api/members/admin/batch`,
        data: { member_ids: Array.from(selectedIds) },
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }
      });
      toast.success(res.data.message || `${count} membre(s) supprime(s)`);
      setSelectedIds(new Set());
      if (onRefresh) onRefresh();
      else if (onMemberUpdated) onMemberUpdated(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Erreur lors de la suppression';
      toast.error(msg);
    } finally {
      setBatchDeleting(false);
    }
  };

  const canDelete = userRole === 'super_admin';

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-4"><div className="h-10 bg-gray-200 rounded-lg animate-pulse flex-1 max-w-md" /><div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse" /></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-lg border animate-pulse">
              <div className="h-10 w-10 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-2"><div className="h-4 bg-gray-200 rounded w-1/3" /><div className="h-3 bg-gray-200 rounded w-1/4" /></div>
              <div className="h-6 w-20 bg-gray-200 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const SortButton = ({ field, label }) => {
    const active = sortField === field;
    return (
      <button onClick={() => handleSort(field)} className={cn("text-[11px] font-medium px-2 py-1 rounded transition-colors", active ? "bg-slate-200 text-slate-800" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100")}>
        {label}
        {active && (sortDirection === 'asc' ? <ChevronUp className="inline h-3 w-3 ml-0.5" /> : <ChevronDown className="inline h-3 w-3 ml-0.5" />)}
      </button>
    );
  };

  const rowProps = {
    getMid, expandedIds, toggleExpand, canDelete, selectedIds, toggleSelect,
    showEntreprise, handleStatusChange, updatingStatusId, showRCDS, showFTPE, showDOS,
    userRole, setFtpeDateMembre, docsSummary, coppamResume, canChangeStatus, showActions,
    onView, setEditContactMembre, onViewDocuments, setSimsDOSMembre, setUploadDocMembre,
    setQualifsMembre, setSubDocsMembre, setDispositifsMembre, handleGenerateReleve,
    generatingReleveId, handleGenerateReleveFtpe, generatingReleveFtpeId,
    setRemiseSolidaireMembre, setSendFTPEMembre, onFTPEAction, onCreateEstimation,
    onSendEmail, onImpersonate, setAssignCreateurMembre, onDelete, authToken};

  return (
    <div className="space-y-4">
      {/* Barre d'action selection multiple */}
      {selectedIds.size > 0 && canDelete && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-red-50 border-2 border-red-200 rounded-lg animate-in fade-in slide-in-from-top-1 duration-200" data-testid="batch-action-bar">
          <Checkbox checked={true} className="data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500" />
          <span className="text-sm font-medium text-red-800">{selectedIds.size} membre(s) selectionne(s)</span>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleBatchDelete}
            disabled={batchDeleting}
            className="ml-auto gap-1.5"
            data-testid="batch-delete-btn"
          >
            {batchDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {batchDeleting ? 'Suppression...' : `Supprimer (${selectedIds.size})`}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds(new Set())}
            className="text-gray-500 hover:text-gray-700 gap-1"
            data-testid="batch-cancel-btn"
          >
            <XCircle className="h-3.5 w-3.5" />
            Annuler
          </Button>
        </div>
      )}

      {/* Barre de recherche et filtres */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Rechercher par nom, email, entreprise..." value={searchQuery} onChange={handleSearchChange}
            className="pl-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500" data-testid="membres-search" />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="membres-filter-status">
              <Filter className="h-4 w-4" />
              Statut: {statusFilter === 'all' ? 'Tous' : STATUS_CONFIG[statusFilter]?.label || statusFilter}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => handleStatusFilterChange('all')}><Users className="h-4 w-4 mr-2" />Tous les statuts</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleStatusFilterChange('actif')}><span className="w-2 h-2 rounded-full mr-2 bg-emerald-500" />Actif</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleStatusFilterChange('en_attente')}><span className="w-2 h-2 rounded-full mr-2 bg-amber-500" />En attente</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleStatusFilterChange('inactif')}><span className="w-2 h-2 rounded-full mr-2 bg-gray-500" />Inactif</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleStatusFilterChange('suspendu')}><span className="w-2 h-2 rounded-full mr-2 bg-red-500" />Suspendu</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {showExportCSV && filteredAndSortedMembres.length > 0 && (
          <Button variant="outline" className="gap-2" onClick={() => exportToCSV(filteredAndSortedMembres, `membres_${new Date().toISOString().split('T')[0]}.csv`)} data-testid="membres-export-csv">
            <FileDown className="h-4 w-4" />Export CSV
          </Button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-gray-500">Afficher</span>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[70px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{PAGE_SIZE_OPTIONS.map(size => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-sm text-gray-500">sur <span className="font-medium text-gray-700">{paginationInfo.totalItems}</span> membre{paginationInfo.totalItems > 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Tri rapide */}
      <div className="flex items-center gap-1 flex-wrap">
        {canDelete && paginationInfo.totalItems > 0 && (
          <Checkbox
            checked={paginationInfo.paginatedItems.length > 0 && paginationInfo.paginatedItems.every(m => selectedIds.has(getMid(m)))}
            onCheckedChange={toggleSelectAll}
            className="mr-2 data-[state=checked]:bg-slate-700 data-[state=checked]:border-slate-700"
            data-testid="select-all-checkbox"
            aria-label="Selectionner tous"
          />
        )}
        <span className="text-[11px] text-slate-400 mr-1">Trier:</span>
        <SortButton field="nom" label="Nom" />
        <SortButton field="entreprise" label="Entreprise" />
        <SortButton field="status" label="Statut" />
        <SortButton field="created_at" label="Date" />
      </div>

      {/* Liste depliable */}
      {paginationInfo.totalItems === 0 ? (
        <div className="bg-white rounded-xl border shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><Users className="h-8 w-8 text-gray-400" /></div>
          <p className="text-gray-700 font-medium mb-1">{emptyMessage}</p>
          <p className="text-sm text-gray-500">{emptySubMessage}</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="membres-list">
          {paginationInfo.paginatedItems.map((membre) => (
            <MembreRow key={getMid(membre)} membre={membre} {...rowProps} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {paginationInfo.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-white rounded-lg border">
          <div className="text-sm text-gray-600">
            Affichage de <span className="font-medium">{paginationInfo.startIndex + 1}</span> a{' '}
            <span className="font-medium">{paginationInfo.endIndex}</span> sur{' '}
            <span className="font-medium">{paginationInfo.totalItems}</span> membres
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={!paginationInfo.hasPrevPage} className="h-8 w-8 p-0"><ChevronsLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => prev - 1)} disabled={!paginationInfo.hasPrevPage} className="h-8 w-8 p-0"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="flex items-center gap-1 px-2">
              {(() => {
                const pages = [];
                const totalPages = paginationInfo.totalPages;
                const current = currentPage;
                if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
                else {
                  pages.push(1);
                  if (current > 3) pages.push('...');
                  for (let i = Math.max(2, current - 1); i <= Math.min(totalPages - 1, current + 1); i++) pages.push(i);
                  if (current < totalPages - 2) pages.push('...');
                  pages.push(totalPages);
                }
                return pages.map((page, idx) => page === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <Button key={page} variant={currentPage === page ? "default" : "outline"} size="sm" onClick={() => setCurrentPage(page)}
                    className={cn("h-8 w-8 p-0", currentPage === page && "bg-slate-700 hover:bg-slate-800")}>{page}</Button>
                ));
              })()}
            </div>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => prev + 1)} disabled={!paginationInfo.hasNextPage} className="h-8 w-8 p-0"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(paginationInfo.totalPages)} disabled={!paginationInfo.hasNextPage} className="h-8 w-8 p-0"><ChevronsRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      <EditContactModal membre={editContactMembre} open={!!editContactMembre} onClose={() => setEditContactMembre(null)} onSave={handleContactUpdated} token={authToken} userRole={userRole} />
      <SendFTPEAttestationModal membre={sendFTPEMembre} open={!!sendFTPEMembre} onClose={() => setSendFTPEMembre(null)} token={authToken} />
      <AssignCreateurModal membre={assignCreateurMembre} open={!!assignCreateurMembre} onClose={() => setAssignCreateurMembre(null)} onSave={handleContactUpdated} token={authToken} />
      <UploadDocumentMembreModal membre={uploadDocMembre} open={!!uploadDocMembre} onClose={() => setUploadDocMembre(null)} token={authToken} userRole={userRole} />
      <QualificationsModal membre={qualifsMembre} open={!!qualifsMembre} onClose={() => setQualifsMembre(null)} token={authToken} userRole={userRole} />
      <UpdateDispositifsModal membre={dispositifsMembre} open={!!dispositifsMembre} onClose={() => setDispositifsMembre(null)} onSave={handleContactUpdated} token={authToken} />
      <EditFtpeDateModal membre={ftpeDateMembre} open={!!ftpeDateMembre} onClose={() => setFtpeDateMembre(null)} onSave={handleContactUpdated} token={authToken} />
      <RemiseSolidaireModal membre={remiseSolidaireMembre} open={!!remiseSolidaireMembre} onClose={() => setRemiseSolidaireMembre(null)} onSave={handleContactUpdated} token={authToken} />
      <MemberSimulationsModal membre={simsDOSMembre} open={!!simsDOSMembre} onClose={() => setSimsDOSMembre(null)} token={authToken} />
      <SubscriptionDocumentsModal membre={subDocsMembre} open={!!subDocsMembre} onClose={() => setSubDocsMembre(null)} token={authToken} userRole={userRole} />
    </div>
  );
}

// Re-export all sub-components for backward compatibility
export { StatusBadge, FTPEBadge, DOSBadge, RCDSBadge, MemberAvatar, STATUS_CONFIG, RCDS_CONFIG, FTPE_CONFIG, DOS_CONFIG, exportToCSV };
